import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

// Import pannellum types
declare global {
  interface Window {
    pannellum: {
      viewer: (containerId: string, config: any) => any;
    };
  }
}

interface PanoramaViewerProps {
  imageUrl: string;
  title: string;
  description?: string;
  autoLoad?: boolean;
  autoRotate?: number;
  className?: string;
}

const PanoramaViewer: React.FC<PanoramaViewerProps> = ({
  imageUrl,
  title,
  description,
  autoLoad = true,
  autoRotate = -2,
  className = ''
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstance = useRef<any>(null);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error' | 'fallback'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const containerId = `panorama-${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    let isComponentMounted = true;

    const loadPannellum = async () => {
      try {
        console.log('Loading panorama with image:', imageUrl);
        
        // First check if image exists
        const imageExists = await checkImageExists(imageUrl);
        if (!imageExists) {
          throw new Error(`Image not found: ${imageUrl}`);
        }

        // Load Pannellum CSS
        await loadCSS();
        
        // Load Pannellum JS
        await loadJS();
        
        if (isComponentMounted) {
          initializePanorama();
        }
      } catch (error) {
        console.error('Error in loadPannellum:', error);
        if (isComponentMounted) {
          setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
          setLoadingState('fallback');
        }
      }
    };

    const checkImageExists = (url: string): Promise<boolean> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });
    };

    const loadCSS = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector('link[href*="pannellum.css"]')) {
          resolve();
          return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
        link.onload = () => {
          console.log('Pannellum CSS loaded');
          resolve();
        };
        link.onerror = () => {
          console.error('Failed to load Pannellum CSS');
          reject(new Error('Failed to load Pannellum CSS'));
        };
        document.head.appendChild(link);
      });
    };

    const loadJS = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (window.pannellum) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
        script.onload = () => {
          console.log('Pannellum JS loaded');
          resolve();
        };
        script.onerror = () => {
          console.error('Failed to load Pannellum JS');
          reject(new Error('Failed to load Pannellum JS from CDN'));
        };
        document.head.appendChild(script);
      });
    };

    const initializePanorama = () => {
      try {
        if (viewerRef.current && window.pannellum && isComponentMounted) {
          console.log('Initializing panorama viewer for container:', containerId);
          
          viewerInstance.current = window.pannellum.viewer(containerId, {
            type: 'equirectangular',
            panorama: imageUrl,
            autoLoad: autoLoad,
            autoRotate: autoRotate,
            showZoomCtrl: true,
            showFullscreenCtrl: true,
            showControls: true,
            keyboardZoom: true,
            mouseZoom: true,
            doubleClickZoom: true,
            draggable: true,
            showTitle: false,
            compass: true,
            northOffset: 0,
            preview: imageUrl,
            loadButtonLabel: 'Click to Load 360° View',
            hotSpotDebug: false,
            escapeKeyQuit: true,
            orientationOnByDefault: false,
            orientationEventTarget: document,
            targetPitch: 0,
            targetYaw: 0,
            targetHfov: 100,
            pitch: 0,
            yaw: 0,
            hfov: 100,
            minHfov: 50,
            maxHfov: 120,
            minPitch: -30,
            maxPitch: 30,
          });

          // Add event listeners
          viewerInstance.current.on('load', () => {
            console.log('Panorama loaded successfully');
            if (isComponentMounted) {
              setLoadingState('loaded');
            }
          });

          viewerInstance.current.on('error', (error: any) => {
            console.error('Panorama viewer error:', error);
            if (isComponentMounted) {
              setErrorMessage('Failed to load panorama viewer');
              setLoadingState('error');
            }
          });
        }
      } catch (error) {
        console.error('Error creating panorama viewer:', error);
        if (isComponentMounted) {
          setErrorMessage('Failed to initialize panorama viewer');
          setLoadingState('error');
        }
      }
    };

    loadPannellum();

    return () => {
      isComponentMounted = false;
      if (viewerInstance.current) {
        try {
          viewerInstance.current.destroy();
        } catch (error) {
          console.warn('Error destroying panorama viewer:', error);
        }
      }
    };
  }, [imageUrl, autoLoad, autoRotate, containerId]);

  const renderContent = () => {
    switch (loadingState) {
      case 'loading':
        return (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dblue mx-auto mb-4"></div>
              <p className="text-gray-600">Loading 360° Classroom View...</p>
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <div className="text-center p-8">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <p className="text-gray-600 mb-2">Unable to load 360° viewer</p>
              <p className="text-sm text-gray-500">{errorMessage}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 bg-dblue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        );
      
      case 'fallback':
        return (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <h3 className="text-lg font-semibold mb-4">{title}</h3>
              <img 
                src={imageUrl} 
                alt={title} 
                className="max-w-full h-auto rounded-lg mx-auto mb-4"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <p className="text-sm text-gray-600">360° view temporarily unavailable</p>
              <p className="text-xs text-gray-500 mt-2">Showing classroom image instead</p>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`bg-white rounded-2xl shadow-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-dblue to-blue-600">
        <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
        {description && (
          <p className="text-blue-100 leading-relaxed">{description}</p>
        )}
        <div className="mt-4 flex items-center text-blue-100 text-sm">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {loadingState === 'loaded' ? 'Drag to look around • Scroll to zoom • Click fullscreen for best experience' : 'Loading interactive 360° view...'}
        </div>
      </div>

      {/* Panorama Viewer */}
      <div className="relative">
        <div
          ref={viewerRef}
          id={containerId}
          className="w-full h-96 md:h-[500px] bg-gray-100"
          style={{ minHeight: '400px' }}
        />
        
        {/* Overlay content based on loading state */}
        {loadingState !== 'loaded' && renderContent()}
      </div>

      {/* Footer with tips */}
      {loadingState === 'loaded' && (
        <div className="p-4 bg-gray-50 border-t">
          <div className="flex flex-wrap items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Interactive 360° View
              </div>
            </div>
            <div className="mt-2 sm:mt-0">
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                ✓ Loaded Successfully
              </span>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PanoramaViewer;
